import React, { useContext, useEffect, useState, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Grid,
  Button,
  Box,
  Typography,
  LinearProgress,
  Chip,
  IconButton,
  Divider,
  Link,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DownloadIcon from '@mui/icons-material/Download';
import UploadIcon from '@mui/icons-material/Upload';
import DeleteIcon from '@mui/icons-material/Delete';
import FolderIcon from '@mui/icons-material/Folder';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ImageIcon from '@mui/icons-material/Image';
import VideoFileIcon from '@mui/icons-material/VideoFile';
import DescriptionIcon from '@mui/icons-material/Description';
import TableChartIcon from '@mui/icons-material/TableChart';
import SlideshowIcon from '@mui/icons-material/Slideshow';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import PageArea from '../../components/shared/PageArea';
import PageTitle from '../../components/shared/PageTitle';
import { UserContext } from '../../components/shared/useUser';
import { Section } from '../../components/ui/Section';
import {
  DocumentsClient,
  DocumentFoldersClient,
  DocumentDto,
  DocumentFolderDto,
  formatFileSize,
} from '../../app/services/api/documentsClient';
import {
  TaskTemplatesClient,
  TaskTemplateDto,
} from '../../app/services/api/tasksClient';
import { DocumentType } from '../../enums/DocumentPermission';

const documentsClient = new DocumentsClient();
const foldersClient = new DocumentFoldersClient();
const templatesClient = TaskTemplatesClient;

const DocumentView: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { hasPermissionTo } = useContext(UserContext);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const canEdit = hasPermissionTo ? hasPermissionTo(['DocumentsEdit']) : true;
  const canDelete = hasPermissionTo ? hasPermissionTo(['DocumentsDelete']) : true;
  const canDownload = hasPermissionTo ? hasPermissionTo(['DocumentsDownload']) : true;

  const [loading, setLoading] = useState(true);
  const [document, setDocument] = useState<DocumentDto | null>(null);
  const [folder, setFolder] = useState<DocumentFolderDto | null>(null);
  const [linkedTemplates, setLinkedTemplates] = useState<TaskTemplateDto[]>([]);

  // Upload new version state
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [changeNotes, setChangeNotes] = useState('');
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    const loadData = async () => {
      if (!id) return;

      try {
        const doc = await documentsClient.getDocument(Number(id));
        setDocument(doc);

        const f = await foldersClient.getFolder(doc.folderId);
        setFolder(f);

        if (doc.linkedTaskTemplateIds && doc.linkedTaskTemplateIds.length > 0) {
          const allTemplates = await templatesClient.getTemplates();
          setLinkedTemplates(allTemplates.filter(t => doc.linkedTaskTemplateIds?.includes(t.id)));
        }
      } catch (error) {
        console.error('Error loading document:', error);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [id]);

  const getDocumentIcon = (documentType: DocumentType) => {
    const iconProps = { sx: { fontSize: 80 } };
    switch (documentType) {
      case DocumentType.PDF:
        return <PictureAsPdfIcon {...iconProps}
          color="error" />;
      case DocumentType.Image:
        return <ImageIcon {...iconProps}
          color="primary" />;
      case DocumentType.Video:
        return <VideoFileIcon {...iconProps}
          color="secondary" />;
      case DocumentType.Document:
        return <DescriptionIcon {...iconProps}
          color="info" />;
      case DocumentType.Spreadsheet:
        return <TableChartIcon {...iconProps}
          color="success" />;
      case DocumentType.Presentation:
        return <SlideshowIcon {...iconProps}
          sx={{ ...iconProps.sx, color: 'orange' }} />;
      default:
        return <InsertDriveFileIcon {...iconProps} />;
    }
  };

  const handleDownload = () => {
    if (!document) return;
    // In a real implementation, this would trigger a download from blob storage
    console.log('Downloading:', document.latestVersion.blobUrl);
    // window.open(document.latestVersion.blobUrl, '_blank');
  };

  const handleOpenUploadDialog = () => {
    setSelectedFile(null);
    setChangeNotes('');
    setUploadDialogOpen(true);
  };

  const handleCloseUploadDialog = () => {
    setUploadDialogOpen(false);
    setSelectedFile(null);
    setChangeNotes('');
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setSelectedFile(file);
    }
  };

  const handleUploadVersion = async () => {
    if (!document || !selectedFile) return;

    setUploading(true);
    try {
      const updatedDoc = await documentsClient.uploadVersion(
        {
          documentId: document.id,
          changeNotes: changeNotes || undefined,
        },
        selectedFile,
      );
      setDocument(updatedDoc);
      handleCloseUploadDialog();
    } catch (error) {
      console.error('Error uploading new version:', error);
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async () => {
    if (!document) return;

    if (window.confirm('Are you sure you want to delete this document?')) {
      try {
        await documentsClient.deleteDocument(document.id);
        navigate(`/documents?folderId=${folder?.id}`);
      } catch (error) {
        console.error('Error deleting document:', error);
      }
    }
  };

  if (loading) {
    return (
      <PageArea>
        <LinearProgress />
      </PageArea>
    );
  }

  if (!document) {
    return (
      <PageArea>
        <PageTitle>Document Not Found</PageTitle>
        <Typography color="error">The requested document could not be found.</Typography>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/documents')}
          sx={{ mt: 2 }}
        >
          Back to Documents
        </Button>
      </PageArea>
    );
  }

  return (
    <PageArea>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
        <IconButton
          aria-label="Back to documents"
          onClick={() => navigate(`/documents?folderId=${folder?.id}`)}
        >
          <ArrowBackIcon />
        </IconButton>
        <Box sx={{ flexGrow: 1 }}>
          <PageTitle subTitle={folder?.path.join(' / ')}>
            {document.name}
          </PageTitle>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {canDownload && (
            <Button
              variant="secondary"
              startIcon={<DownloadIcon />}
              onClick={handleDownload}
            >
              Download
            </Button>
          )}
          {canEdit && (
            <Button
              variant="secondary"
              startIcon={<UploadIcon />}
              onClick={handleOpenUploadDialog}
            >
              Upload New Version
            </Button>
          )}
          {canDelete && (
            <Button
              variant="secondary"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={handleDelete}
            >
              Delete
            </Button>
          )}
        </Box>
      </Box>

      <Grid
        container
        spacing={3}
      >
        {/* Preview Area */}
        <Grid item
          xs={12}
          md={8}>
          <Section>
            <Box sx={{ p: 4, textAlign: 'center', minHeight: 400, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
              {/* Image Viewer */}
              {document.latestVersion.documentType === DocumentType.Image && document.latestVersion.blobUrl ? (
                <Box sx={{ width: '100%', maxWidth: '100%', position: 'relative' }}>
                  <Box
                    component="img"
                    src={document.latestVersion.blobUrl || document.latestVersion.thumbnailUrl || ''}
                    alt={document.name}
                    sx={{
                      maxWidth: '100%',
                      maxHeight: '70vh',
                      objectFit: 'contain',
                      borderRadius: 1,
                      cursor: 'pointer',
                      transition: 'transform 0.2s',
                      '&:hover': {
                        transform: 'scale(1.02)',
                      },
                    }}
                    onClick={() => window.open(document.latestVersion.blobUrl || undefined, '_blank')}
                  />
                  <Typography
                    variant="textXS"
                    color="text.secondary"
                    sx={{ mt: 1 }}
                  >
                    Click image to view full size in new tab
                  </Typography>
                </Box>
              ) : document.latestVersion.documentType === DocumentType.Image && document.latestVersion.thumbnailUrl ? (
                <Box sx={{ width: '100%', maxWidth: '100%' }}>
                  <Box
                    component="img"
                    src={document.latestVersion.thumbnailUrl}
                    alt={document.name}
                    sx={{ maxWidth: '100%', maxHeight: '70vh', objectFit: 'contain', borderRadius: 1 }}
                  />
                  <Typography
                    variant="textXS"
                    color="text.secondary"
                    sx={{ mt: 1 }}
                  >
                    Preview only - download for full resolution
                  </Typography>
                </Box>
              ) : document.latestVersion.documentType === DocumentType.Image ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <ImageIcon sx={{ fontSize: 80, color: 'primary.main', mb: 2 }} />
                  <Typography variant="displayXS">Image Preview Unavailable</Typography>
                  <Typography color="text.secondary"
                    sx={{ mb: 2 }}>
                    {document.latestVersion.fileName}
                  </Typography>
                  {canDownload && (
                    <Button
                      variant="primary"
                      startIcon={<DownloadIcon />}
                      onClick={handleDownload}
                    >
                      Download to View
                    </Button>
                  )}
                </Box>
              ) : /* Video Player */
                document.latestVersion.documentType === DocumentType.Video && document.latestVersion.blobUrl ? (
                  <Box sx={{ width: '100%', maxWidth: '100%' }}>
                    <Box
                      component="video"
                      controls
                      src={document.latestVersion.blobUrl}
                      sx={{
                        width: '100%',
                        height: 'auto',
                        minHeight: '60vh',
                        maxHeight: 'none',
                        borderRadius: 1,
                        backgroundColor: 'black',
                      }}
                    >
                      Your browser does not support the video tag.
                    </Box>
                    <Box sx={{ mt: 2, textAlign: 'center' }}>
                      <Typography
                        variant="textSM"
                        fontWeight={500}
                      >
                        {document.latestVersion.fileName}
                      </Typography>
                      <Typography
                        variant="textXS"
                        color="text.secondary"
                        sx={{ mt: 0.5 }}
                      >
                        {formatFileSize(document.latestVersion.fileSize)}
                      </Typography>
                    </Box>
                  </Box>
                ) : document.latestVersion.documentType === DocumentType.Video ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <VideoFileIcon sx={{ fontSize: 80, color: 'secondary.main', mb: 2 }} />
                    <Typography variant="displayXS">Video Preview Unavailable</Typography>
                    <Typography color="text.secondary"
                      sx={{ mb: 2 }}>
                      {document.latestVersion.fileName}
                    </Typography>
                    {canDownload && (
                      <Button
                        variant="primary"
                        startIcon={<DownloadIcon />}
                        onClick={handleDownload}
                      >
                        Download to View
                      </Button>
                    )}
                  </Box>
                ) : /* PDF and Other Files */
                  document.latestVersion.documentType === DocumentType.PDF && document.latestVersion.blobUrl ? (
                    <Box sx={{ width: '100%', height: '70vh' }}>
                      <iframe
                        src={document.latestVersion.blobUrl}
                        title={document.name}
                        style={{
                          width: '100%',
                          height: '100%',
                          border: 'none',
                          borderRadius: '4px',
                        }}
                      />
                    </Box>
                  ) : (
                    <>
                      {getDocumentIcon(document.latestVersion.documentType)}
                      <Typography
                        variant="displayXS"
                        sx={{ mt: 2 }}
                      >
                        {document.latestVersion.fileName}
                      </Typography>
                      <Typography color="text.secondary">
                        {formatFileSize(document.latestVersion.fileSize)}
                      </Typography>
                      {canDownload && (
                        <Button
                          variant="primary"
                          startIcon={<DownloadIcon />}
                          onClick={handleDownload}
                          sx={{ mt: 2 }}
                        >
                          Download to View
                        </Button>
                      )}
                    </>
                  )}
            </Box>
          </Section>
        </Grid>

        {/* Details Panel */}
        <Grid item
          xs={12}
          md={4}>
          <Section>
            <Box sx={{ p: 4 }}>
              <Typography
                variant="displayXS"
                gutterBottom
                sx={{ margin: 4 }}
              >
                Document Details
              </Typography>
              <Divider sx={{ mb: 2 }} />


              {document.description && (
                <Box sx={{ margin: 4, mt: 2 }}>
                  <Typography
                    variant="textSM"
                    color="text.secondary"
                  >
                    {document.description}
                  </Typography>
                </Box>
              )}

              <Table size="small">
                <TableBody>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600, border: 0 }}>Type</TableCell>
                    <TableCell sx={{ border: 0 }}>
                      <Chip size="small"
                        label={document.latestVersion.documentType} />
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600, border: 0 }}>Size</TableCell>
                    <TableCell sx={{ border: 0 }}>{formatFileSize(document.latestVersion.fileSize)}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600, border: 0 }}>Version</TableCell>
                    <TableCell sx={{ border: 0 }}>v{document.versionCount} ({document.versionCount} version{document.versionCount > 1 ? 's' : ''})</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600, border: 0 }}>Uploaded</TableCell>
                    <TableCell sx={{ border: 0 }}>
                      {new Date(document.createdAt).toLocaleString()}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600, border: 0 }}>Uploaded By</TableCell>
                    <TableCell sx={{ border: 0 }}>{document.createdByUserName ?? 'Unknown'}</TableCell>
                  </TableRow>
                  {document.updatedAt && (
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600, border: 0 }}>Last Modified</TableCell>
                      <TableCell sx={{ border: 0 }}>
                        {new Date(document.updatedAt).toLocaleString()}
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>


              {document.tags && document.tags.length > 0 && (
                <Box sx={{ margin: 4 }}>
                  <Typography
                    variant="textMD"
                    gutterBottom
                  >
                    Tags
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {document.tags.map(tag => (
                      <Chip key={tag}
                        size="small"
                        label={tag}
                        variant="outlined" />
                    ))}
                  </Box>
                </Box>
              )}
            </Box>
          </Section>

          {/* Folder Location */}
          {folder && (
            <Section style={{ mt: 4, p: 4 }}>
              <Box sx={{ p: 2 }}>
                <Typography
                  variant="displayXS"
                  gutterBottom
                  sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                >
                  <FolderIcon />
                  Location
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Link
                  component="button"
                  underline="hover"
                  onClick={() => navigate(`/documents?folderId=${folder.id}`)}
                  sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                >
                  {folder.path.join(' / ')}
                </Link>
              </Box>
            </Section>
          )}
        </Grid>
      </Grid>

      {/* Upload New Version Dialog */}
      <Dialog
        open={uploadDialogOpen}
        onClose={handleCloseUploadDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Upload New Version</DialogTitle>
        <DialogContent>
          <Typography
            variant="textSM"
            color="text.secondary"
            sx={{ mb: 2 }}
          >
            Upload a new version of &quot;{document?.name}&quot;. The current version is v{document?.versionCount}.
          </Typography>

          <input
            ref={fileInputRef}
            aria-label="Select document version file"
            type="file"
            onChange={handleFileSelect}
            style={{ display: 'none' }}
            id="version-file-input"
          />

          <Box
            component="label"
            htmlFor="version-file-input"
            sx={{
              border: '2px dashed',
              borderColor: selectedFile ? 'primary.main' : 'divider',
              borderRadius: 2,
              p: 3,
              textAlign: 'center',
              cursor: 'pointer',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              mb: 2,
              '&:hover': {
                borderColor: 'primary.main',
                bgcolor: 'action.hover',
              },
            }}
          >
            {selectedFile ? (
              <>
                <InsertDriveFileIcon sx={{ fontSize: 48, color: 'primary.main', mb: 1 }} />
                <Typography
                  variant="textMD"
                  fontWeight={500}
                >
                  {selectedFile.name}
                </Typography>
                <Typography
                  variant="textSM"
                  color="text.secondary"
                >
                  {formatFileSize(selectedFile.size)}
                </Typography>
              </>
            ) : (
              <>
                <CloudUploadIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
                <Typography
                  variant="textMD"
                  color="text.secondary"
                >
                  Click to select a file
                </Typography>
              </>
            )}
          </Box>

          <TextField
            label="Change Notes (optional)"
            placeholder="Describe what changed in this version..."
            value={changeNotes}
            onChange={(e) => setChangeNotes(e.target.value)}
            fullWidth
            multiline
            rows={3}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCloseUploadDialog}
            disabled={uploading}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleUploadVersion}
            disabled={!selectedFile || uploading}
            startIcon={uploading ? undefined : <UploadIcon />}
          >
            {uploading ? 'Uploading...' : 'Upload Version'}
          </Button>
        </DialogActions>
      </Dialog>
    </PageArea>
  );
};

export default DocumentView;